import API_BASE from '../config';
import { creditAwareFetch } from '../hooks/creditAwareFetch';
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/clerk-react';
import UserManagement from '../components/UserManagement';
import SequenceConfigPanel from '../components/SequenceConfigPanel';


const SECTION_ACCENTS = {
  discovery: '#1A56A0', icp: '#8B1A1A', prospects: '#276749',
  sequences: '#B7791F', 'linkedin-batches': '#0A66C2', 'email-batches': '#1A56A0',
  'mail/inbox': '#2D3748', intelligence: '#6B46C1', activity: '#4A5568',
  usage: '#5A5856', webhooks: '#374151'
};

function ClientInitial({ name, website }) {
  const initials = name ? name.split(' ').map(w => w[0]).slice(0, 2).join('') : '?';
  const domain = website ? website.replace(/^https?:\/\//, '').replace(/\/.*$/, '') : null;
  const faviconUrl = domain ? 'https://www.google.com/s2/favicons?domain=' + domain + '&sz=64' : null;
  if (faviconUrl) return <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#F5E6E6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' }}><img src={faviconUrl} alt={name} style={{ width: '32px', height: '32px', objectFit: 'contain' }} onError={e => e.target.style.display='none'} /></div>;
  return <div style={{ width: '44px', height: '44px', borderRadius: '10px', background: '#F5E6E6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '500', color: '#8B1A1A', flexShrink: 0 }}>{initials}</div>;
}

function ClientWorkspace() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const role = user?.publicMetadata?.role;
  const isSuperAdmin = role === 'super_admin' || role === 'team_member';
  const [client, setClient] = useState(null);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [deactivatingUser, setDeactivatingUser] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const [showUserForm, setShowUserForm] = useState(false);
  const [userForm, setUserForm] = useState({ email: '', name: '', role: 'client' });
  const [creatingUser, setCreatingUser] = useState(null);
  const [userError, setUserError] = useState(null);
  const [userSuccess, setUserSuccess] = useState(null);
  const [revokingUser, setRevokingUser] = useState(null);

  useEffect(() => { fetchClient(); fetchUsers(); }, [id]);

  async function fetchClient() {
    try {
      const res = await fetch(`${API_BASE}/clients/${id}`);
      setClient(await res.json());
    } catch (err) { setError('Could not load client.'); }
  }

  async function fetchUsers() {
    try {
      const res = await fetch(`${API_BASE}/clients/${id}/users`);
      const data = await res.json();
      setUsers(Array.isArray(data) ? data : []);
    } catch (e) {}
  }

  async function handleCreateUser() {
    if (!userForm.email.trim()) return;
    setCreatingUser(true);
    setUserError(null);
    setUserSuccess(null);
    try {
      const res = await creditAwareFetch(`${API_BASE}/users/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...userForm, client_id: id })
      });
      if (!res) return; // 402 limit_reached — modal handled by creditAwareFetch
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setUserSuccess(`Login email sent to ${userForm.email}`);
      setUserForm({ email: '', name: '', role: 'client' });
      setShowUserForm(false);
      await fetchUsers();
    } catch (e) { setUserError(e.message); }
    setCreatingUser(false);
  }

  async function handleRevokeUser(clerkUserId) {
    setRevokingUser(clerkUserId);
    try {
      await fetch(`${API_BASE}/users/${clerkUserId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      await fetchUsers();
    } catch (e) {}
    setRevokingUser(null);
    setConfirmRemove(null);
  }

  async function handleDeactivate(clerkUserId) {
    setDeactivatingUser(clerkUserId);
    try {
      await fetch(`${API_BASE}/users/${clerkUserId}/deactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      await fetchUsers();
    } catch (e) {}
    setDeactivatingUser(null);
  }

  async function handleReactivate(clerkUserId) {
    setDeactivatingUser(clerkUserId);
    try {
      await fetch(`${API_BASE}/users/${clerkUserId}/reactivate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      await fetchUsers();
    } catch (e) {}
    setDeactivatingUser(null);
  }

  if (error) return <div style={{ padding: '2rem' }}><p style={{ color: '#C53030' }}>{error}</p></div>;
  if (!client) return (
    <div>
      
      <div style={{ padding: '2rem' }}>{[1,2,3].map(i => <div key={i} style={{ height: '60px', background: '#EEECEA', borderRadius: '10px', marginBottom: '0.75rem' }} />)}</div>
    </div>
  );

  const sections = [
    { label: 'Discovery Brief', path: 'discovery', description: 'Notes and context from your discovery sessions.' },
    { label: 'ICP', path: 'icp', description: 'Ideal customer profile — generated and editable.' },
    { label: 'Prospects', path: 'prospects', description: 'Companies and contacts sourced from Apollo.' },
    { label: 'Sequences', path: 'sequences', description: 'AI-generated email and LinkedIn sequences per contact.' },
    { label: 'LinkedIn Batches', path: 'linkedin-batches', description: 'Group approved LinkedIn sequences and export ready for upload.' },
    { label: 'Email Batches', path: 'email-batches', description: 'Group approved email sequences and export as a CSV ready to import into your CRM.' },
    { label: 'Mail', path: 'mail/inbox', description: 'Inbox, campaigns, mailboxes, and email metrics for this client.' },
    { label: 'Intelligence', path: 'intelligence', description: 'Market signals, tenders, and news digests.' },
    { label: 'Activity Log', path: 'activity', description: 'Full timestamped trail of actions for this client.' },
    { label: 'Usage', path: 'usage', description: 'Credit balance, top-ups, and transaction history.' },
    { label: 'Webhooks', path: 'webhooks', description: 'Send events to Zapier, Make, or your own endpoint.' },
  ];

  const ROLE_LABELS = { client: 'Workspace Admin', team_member: 'Workspace Member', super_admin: 'Super Admin' };
  const ROLE_COLOURS = { client: '#2B6CB0', team_member: '#276749', super_admin: '#8B1A1A' };
  const ROLE_BG = { client: '#EBF8FF', team_member: '#F0FFF4', super_admin: '#FFF5F5' };

  return (
    <div>

      <div style={{ padding: '2rem', maxWidth: '1100px', margin: '0 auto', boxSizing: 'border-box' }}>

        <button onClick={() => navigate(isSuperAdmin ? '/clients' : ('/clients/' + (client?.id || '') + '/account?tab=workspaces'))} style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.83rem', cursor: 'pointer', padding: 0, marginBottom: '1.5rem' }}>
          ← {isSuperAdmin ? 'All clients' : 'Back to workspaces'}
        </button>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <ClientInitial name={client.name} website={client.website} />
            <div>
              <h1 style={{ fontSize: '1.4rem', fontWeight: '500', margin: '0 0 0.2rem', color: '#1A1A1A' }}>{client.name}</h1>
              {client.account_id && client.account_name && (
                <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: '#888' }}>
                  Part of {isSuperAdmin
                    ? <span onClick={e => { e.stopPropagation(); navigate('/admin/accounts/' + client.account_id); }} style={{ color: '#2B6CB0', cursor: 'pointer', textDecoration: 'underline' }}>{client.account_name}</span>
                    : <span style={{ color: '#1A1A1A' }}>{client.account_name}</span>}
                </p>
              )}
              <p style={{ margin: 0, fontSize: '0.83rem', color: '#888' }}>{client.sector || 'No sector'}</p>
            </div>
          </div>
          <span style={{ fontSize: '0.72rem', backgroundColor: '#F0FFF4', color: '#276749', padding: '0.2rem 0.6rem', borderRadius: '20px', fontWeight: '500' }}>{client.status}</span>
        </div>

        {/* Workspace sections */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '0.75rem', marginBottom: '2.5rem' }}>
          {sections.map(section => (
            <div key={section.path} onClick={() => navigate(`/clients/${id}/${section.path}`)}
              style={{ backgroundColor: SECTION_ACCENTS[section.path], borderRadius: '10px', padding: '1.25rem 1.5rem', cursor: 'pointer', transition: 'opacity 0.15s' }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
              <p style={{ margin: '0 0 0.4rem', fontWeight: '500', fontSize: '0.95rem', color: '#fff' }}>{section.label}</p>
              <p style={{ margin: 0, fontSize: '0.8rem', color: 'rgba(255,255,255,0.75)', lineHeight: '1.5' }}>{section.description}</p>
            </div>
          ))}
        </div>

        {/* Sequence settings (client-level) */}
        <div style={{ borderTop: '0.5px solid #E8E4E0', paddingTop: '2rem', marginBottom: '2rem' }}>
          <SequenceConfigPanel level="client" id={id} />
        </div>

        {/* User Management */}
        <div style={{ borderTop: '0.5px solid #E8E4E0', paddingTop: '2rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <div>
              <h2 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: '500', color: '#1A1A1A' }}>Users</h2>
              <p style={{ margin: 0, fontSize: '0.82rem', color: '#888' }}>Give {client.name} team members access to their workspace in Client Engine</p>
            </div>
            <button onClick={() => setShowUserForm(!showUserForm)}
              style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500' }}>
              + Add user
            </button>
          </div>

          {userSuccess && <p style={{ color: '#276749', fontSize: '0.85rem', backgroundColor: '#F0FFF4', padding: '0.6rem 1rem', borderRadius: '6px', marginBottom: '1rem' }}>{userSuccess}</p>}
          {userError && <p style={{ color: '#C53030', fontSize: '0.85rem', backgroundColor: '#FFF5F5', padding: '0.6rem 1rem', borderRadius: '6px', marginBottom: '1rem' }}>{userError}</p>}

          {showUserForm && (
            <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderLeft: '3px solid #8B1A1A', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem', marginBottom: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Email *</label>
                  <input value={userForm.email} onChange={e => setUserForm({...userForm, email: e.target.value})} placeholder="name@company.com"
                    style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Name</label>
                  <input value={userForm.name} onChange={e => setUserForm({...userForm, name: e.target.value})} placeholder="Full name"
                    style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.8rem', color: '#4A5568', marginBottom: '0.3rem', fontWeight: '500' }}>Role</label>
                  <select value={userForm.role} onChange={e => setUserForm({...userForm, role: e.target.value})}
                    style={{ width: '100%', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 0.75rem', fontSize: '0.85rem', boxSizing: 'border-box' }}>
                    <option value="client">Workspace Admin</option>
                    <option value="team_member">Workspace Member</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                <button onClick={handleCreateUser} disabled={creatingUser || !userForm.email.trim()}
                  style={{ backgroundColor: '#8B1A1A', color: 'white', border: 'none', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', opacity: creatingUser || !userForm.email.trim() ? 0.5 : 1 }}>
                  {creatingUser ? 'Creating & sending email...' : 'Create user & send invite'}
                </button>
                <button onClick={() => setShowUserForm(false)}
                  style={{ backgroundColor: 'transparent', color: '#4A5568', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            </div>
          )}

          {users.length === 0 ? (
            <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
              <p style={{ margin: 0, fontSize: '0.85rem', color: '#AAA' }}>No users yet. Add one above to give them access to this workspace.</p>
            </div>
          ) : (
            <div style={{ backgroundColor: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', overflow: 'hidden' }}>
              {users.map((u, i) => (
                <div key={u.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1.25rem', borderBottom: i < users.length - 1 ? '0.5px solid #F0EDE9' : 'none' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>{u.name || u.email}</p>
                    <p style={{ margin: '0.1rem 0 0', fontSize: '0.75rem', color: '#888' }}>{u.email} · Added {new Date(u.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.72rem', fontWeight: '500', color: ROLE_COLOURS[u.role], backgroundColor: ROLE_BG[u.role], padding: '0.2rem 0.6rem', borderRadius: '20px' }}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                    {u.deactivated_at && (
                      <span style={{ fontSize: '0.72rem', fontWeight: '500', color: '#888', backgroundColor: '#F0EDE9', padding: '0.2rem 0.6rem', borderRadius: '20px' }}>
                        Deactivated
                      </span>
                    )}
                    {u.deactivated_at ? (
                      <button onClick={() => handleReactivate(u.clerk_user_id)} disabled={deactivatingUser === u.clerk_user_id}
                        style={{ fontSize: '0.75rem', color: '#276749', background: 'none', border: '1px solid #9AE6B4', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                        {deactivatingUser === u.clerk_user_id ? 'Working...' : 'Reactivate'}
                      </button>
                    ) : (
                      <button onClick={() => handleDeactivate(u.clerk_user_id)} disabled={deactivatingUser === u.clerk_user_id}
                        style={{ fontSize: '0.75rem', color: '#4A5568', background: 'none', border: '1px solid #D4CFC9', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                        {deactivatingUser === u.clerk_user_id ? 'Working...' : 'Deactivate'}
                      </button>
                    )}
                    <button onClick={() => setConfirmRemove(u)} disabled={revokingUser === u.clerk_user_id}
                      style={{ fontSize: '0.75rem', color: '#C53030', background: 'none', border: '1px solid #FEB2B2', borderRadius: '6px', padding: '0.2rem 0.6rem', cursor: 'pointer' }}>
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      {confirmRemove && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300 }} onClick={() => setConfirmRemove(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: '10px', padding: '1.5rem', maxWidth: '440px', width: '90%', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}>
            <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: '600', color: '#1A1A1A' }}>Remove user?</h3>
            <p style={{ margin: '0 0 1rem', fontSize: '0.85rem', color: '#4A5568', lineHeight: '1.5' }}>
              This will permanently delete <strong>{confirmRemove.email}</strong> from Clerk and from this workspace. Audit trail entries created by this user will remain. This cannot be undone.
            </p>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.82rem', color: '#888' }}>
              If you only want to block access temporarily, use Deactivate instead.
            </p>
            <div style={{ display: 'flex', gap: '0.6rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setConfirmRemove(null)}
                style={{ background: 'none', border: '1px solid #D4CFC9', color: '#4A5568', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
              <button onClick={() => handleRevokeUser(confirmRemove.clerk_user_id)} disabled={revokingUser === confirmRemove.clerk_user_id}
                style={{ background: '#C53030', border: 'none', color: '#fff', borderRadius: '6px', padding: '0.5rem 1rem', fontSize: '0.85rem', cursor: 'pointer', fontWeight: '500' }}>
                {revokingUser === confirmRemove.clerk_user_id ? 'Removing...' : 'Remove permanently'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientWorkspace;
